import { isPS } from '@/utils';
import type { AxiosError } from 'axios';
import {
  type ContextType,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

type PublicSiteStatus = 'open' | 'maintenance' | 'sorry';

const Context = createContext<
  | {
      publicSiteStatus: PublicSiteStatus;
      setPublicSiteStatus: Dispatch<SetStateAction<PublicSiteStatus>>;
    }
  | undefined
>(undefined);

const _useContext = (): NonNullable<ContextType<typeof Context>> => {
  const context = useContext(Context);
  if (!context) {
    throw new Error(
      'usePublicSiteStatus must be used within PublicSiteStatusProvider.',
    );
  }
  return context;
};

export const usePublicSiteStatus = () => {
  const { publicSiteStatus, setPublicSiteStatus } = _useContext();

  const handleAxiosError = useCallback(
    (err: AxiosError<any, any>) => {
      if (isPS() && err.response) {
        const { status } = err.response;
        if (
          status === 403 &&
          err.response.data?.error_code === 'IP_RESTRICTED'
        ) {
          setPublicSiteStatus('maintenance');
        } else if (500 <= status && status < 600) {
          setPublicSiteStatus('sorry');
        }
      }
    },
    [setPublicSiteStatus],
  );

  return {
    publicSiteStatus,
    handleAxiosError,
    setPublicSiteStatus,
  };
};

export const PublicSiteStatusProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [publicSiteStatus, setPublicSiteStatus] =
    useState<PublicSiteStatus>('open');

  return (
    <Context.Provider value={{ publicSiteStatus, setPublicSiteStatus }}>
      {children}
    </Context.Provider>
  );
};
